import { type ComponentPropsWithoutRef } from 'react';
import { motion } from 'motion/react';
import { type StripMotionCollisions, useStaggerEntrance } from '../staggerEntrance';

type AnimatedGridItemProps = StripMotionCollisions<ComponentPropsWithoutRef<'div'>> & {
  /** Position in the grid — drives the staggered entrance delay. */
  index: number;
};

/**
 * Grid cell with a staggered "swipe down" entrance: each cell fades and drops
 * in from just above its resting spot, slightly after the previous one, on the
 * shared list cadence (so a large grid stays snappy — the delay plateaus after
 * `listStagger.maxSteps`). The counterpart to {@link AnimatedTableRow}, which
 * rises from below; this drops from above. Honors `prefers-reduced-motion` by
 * rendering a plain, static `<div>`.
 */
export function AnimatedGridItem({ index, ...rest }: AnimatedGridItemProps) {
  const { shouldReduceMotion, transition } = useStaggerEntrance(index);

  if (shouldReduceMotion) {
    return <div {...rest} />;
  }

  return (
    <motion.div
      {...rest}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={transition}
    />
  );
}

AnimatedGridItem.displayName = 'AnimatedGridItem';
